/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AnagraficaApplicazione,
  GetApplicazioniParams,
  GetApplicazioniUrlApplicazioneParams,
  WsRispostaPaginataListAnagraficaApplicazione,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce anagraficaApplicazione per id
 * @summary Restituisce anagraficaApplicazione per id
 */
export const getApplicazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AnagraficaApplicazione>(
    { url: `/framework-cfg-utenti/applicazioni/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetApplicazioniIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/applicazioni/${id}`] as const;

export const getGetApplicazioniIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getApplicazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getApplicazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getApplicazioniId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApplicazioniIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getApplicazioniId>>
  > = ({ signal }) => getApplicazioniId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetApplicazioniIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getApplicazioniId>>
>;
export type GetApplicazioniIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce anagraficaApplicazione per id
 */
export const useGetApplicazioniId = <
  TData = Awaited<ReturnType<typeof getApplicazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getApplicazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApplicazioniIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna anagraficaApplicazione
 * @summary Aggiorna anagraficaApplicazione
 */
export const putApplicazioniId = (
  id: string,
  anagraficaApplicazione: AnagraficaApplicazione,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<AnagraficaApplicazione>(
    {
      url: `/framework-cfg-utenti/applicazioni/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: anagraficaApplicazione,
    },
    options,
  );
};

export const getPutApplicazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApplicazioniId>>,
    TError,
    { id: string; data: AnagraficaApplicazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApplicazioniId>>,
  TError,
  { id: string; data: AnagraficaApplicazione },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putApplicazioniId>>,
    { id: string; data: AnagraficaApplicazione }
  > = props => {
    const { id, data } = props ?? {};

    return putApplicazioniId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApplicazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putApplicazioniId>>
>;
export type PutApplicazioniIdMutationBody = AnagraficaApplicazione;
export type PutApplicazioniIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna anagraficaApplicazione
 */
export const usePutApplicazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApplicazioniId>>,
    TError,
    { id: string; data: AnagraficaApplicazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutApplicazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancellazione anagraficaApplicazione
 * @summary Cancellazione anagraficaApplicazione
 */
export const deleteApplicazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/applicazioni/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteApplicazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteApplicazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteApplicazioniId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteApplicazioniId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteApplicazioniId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteApplicazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteApplicazioniId>>
>;

export type DeleteApplicazioniIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancellazione anagraficaApplicazione
 */
export const useDeleteApplicazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteApplicazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteApplicazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cerca una serie di anagraficaApplicazioni. Ordinabile per id, denominazione, urlApplicazione, flApplicazionePrincipale, dataUltimaModifica.
 * @summary Cerca una serie di anagraficaApplicazioni
 */
export const getApplicazioni = (
  params?: GetApplicazioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListAnagraficaApplicazione>(
    {
      url: `/framework-cfg-utenti/applicazioni`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetApplicazioniQueryKey = (params?: GetApplicazioniParams) =>
  [`/framework-cfg-utenti/applicazioni`, ...(params ? [params] : [])] as const;

export const getGetApplicazioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getApplicazioni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetApplicazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getApplicazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getApplicazioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApplicazioniQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApplicazioni>>> = ({
    signal,
  }) => getApplicazioni(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetApplicazioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getApplicazioni>>
>;
export type GetApplicazioniQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di anagraficaApplicazioni
 */
export const useGetApplicazioni = <
  TData = Awaited<ReturnType<typeof getApplicazioni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetApplicazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getApplicazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApplicazioniQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea anagraficaApplicazione se non esiste
 * @summary Creazione anagraficaApplicazione
 */
export const postApplicazioni = (
  anagraficaApplicazione: AnagraficaApplicazione,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<AnagraficaApplicazione>(
    {
      url: `/framework-cfg-utenti/applicazioni`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: anagraficaApplicazione,
    },
    options,
  );
};

export const getPostApplicazioniMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApplicazioni>>,
    TError,
    { data: AnagraficaApplicazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postApplicazioni>>,
  TError,
  { data: AnagraficaApplicazione },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postApplicazioni>>,
    { data: AnagraficaApplicazione }
  > = props => {
    const { data } = props ?? {};

    return postApplicazioni(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApplicazioniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postApplicazioni>>
>;
export type PostApplicazioniMutationBody = AnagraficaApplicazione;
export type PostApplicazioniMutationError = ErrorType<unknown>;

/**
 * @summary Creazione anagraficaApplicazione
 */
export const usePostApplicazioni = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApplicazioni>>,
    TError,
    { data: AnagraficaApplicazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostApplicazioniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce anagraficaApplicazione ricercato per urlApplicazione
 * @summary Restituisce anagraficaApplicazione ricercato per urlApplicazione
 */
export const getApplicazioniUrlApplicazione = (
  params: GetApplicazioniUrlApplicazioneParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AnagraficaApplicazione>(
    {
      url: `/framework-cfg-utenti/applicazioni/url-applicazione`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetApplicazioniUrlApplicazioneQueryKey = (
  params: GetApplicazioniUrlApplicazioneParams,
) =>
  [
    `/framework-cfg-utenti/applicazioni/url-applicazione`,
    ...(params ? [params] : []),
  ] as const;

export const getGetApplicazioniUrlApplicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getApplicazioniUrlApplicazione>>,
  TError = ErrorType<unknown>,
>(
  params: GetApplicazioniUrlApplicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getApplicazioniUrlApplicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getApplicazioniUrlApplicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetApplicazioniUrlApplicazioneQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getApplicazioniUrlApplicazione>>
  > = ({ signal }) =>
    getApplicazioniUrlApplicazione(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetApplicazioniUrlApplicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getApplicazioniUrlApplicazione>>
>;
export type GetApplicazioniUrlApplicazioneQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce anagraficaApplicazione ricercato per urlApplicazione
 */
export const useGetApplicazioniUrlApplicazione = <
  TData = Awaited<ReturnType<typeof getApplicazioniUrlApplicazione>>,
  TError = ErrorType<unknown>,
>(
  params: GetApplicazioniUrlApplicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getApplicazioniUrlApplicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApplicazioniUrlApplicazioneQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l'anagrafica applicazione partendo dalla request http server name
 * @summary Restituisce l'anagrafica applicazione automaticamente dalla request
 */
export const getApplicazioniAnagraficaApplicazione = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AnagraficaApplicazione>(
    {
      url: `/framework-cfg-utenti/applicazioni/anagrafica-applicazione`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetApplicazioniAnagraficaApplicazioneQueryKey = () =>
  [`/framework-cfg-utenti/applicazioni/anagrafica-applicazione`] as const;

export const getGetApplicazioniAnagraficaApplicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getApplicazioniAnagraficaApplicazione>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getApplicazioniAnagraficaApplicazione>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getApplicazioniAnagraficaApplicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetApplicazioniAnagraficaApplicazioneQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getApplicazioniAnagraficaApplicazione>>
  > = ({ signal }) =>
    getApplicazioniAnagraficaApplicazione(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetApplicazioniAnagraficaApplicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getApplicazioniAnagraficaApplicazione>>
>;
export type GetApplicazioniAnagraficaApplicazioneQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce l'anagrafica applicazione automaticamente dalla request
 */
export const useGetApplicazioniAnagraficaApplicazione = <
  TData = Awaited<ReturnType<typeof getApplicazioniAnagraficaApplicazione>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getApplicazioniAnagraficaApplicazione>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetApplicazioniAnagraficaApplicazioneQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
